import { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    message,
    Table,
    Tag,
    Select,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Drawer,
    Descriptions,
    Space,
    Spin,
    Statistic,
    Alert,
} from "antd";
import {
    list_user_subscriptionsApi,
    assign_user_subscriptionApi,
    get_subscription_plansApi,
    cancel_user_subscriptionApi,
    update_user_subscription_quotaApi,
    renew_user_subscriptionApi,
    get_user_subscriptionApi,
    list_billing_audit_logsApi,
    run_billing_cronApi,
    get_billing_cron_healthApi,
    get_billing_metricsApi,
    list_billing_cron_runsApi,
    refund_paymentApi,
    resolve_billing_disputeApi,
    list_chargebacksApi,
    create_enterprise_subscriptionApi,
    activate_enterprise_subscriptionApi,
} from "../api/admin.billing.apis";

const { Option } = Select;

const STATUS_COLORS = {
    active: "green",
    past_due: "orange",
    disputed: "red",
    canceled: "default",
    expired: "default",
    pending: "blue",
    pending_payment: "gold",
};

function UserSubscriptionsManagement() {
    const [subscriptions, setSubscriptions] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [filters, setFilters] = useState({
        plan_id: undefined,
        status: "active",
        expiring_in_days: undefined,
    });
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [cancelTarget, setCancelTarget] = useState({ userId: null, immediate: false });
    const [cancelForm] = Form.useForm();
    const [quotaForm] = Form.useForm();
    const [quotaModalVisible, setQuotaModalVisible] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [cronLoading, setCronLoading] = useState(false);
    const [form] = Form.useForm();

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [detailSubscription, setDetailSubscription] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [billingMetrics, setBillingMetrics] = useState(null);
    const [cronHealth, setCronHealth] = useState(null);
    const [cronRuns, setCronRuns] = useState([]);
    const [cronRunsPagination, setCronRunsPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [cronRunsLoading, setCronRunsLoading] = useState(false);
    const [cronRunsStatusFilter, setCronRunsStatusFilter] = useState(undefined);
    const [enterpriseModalVisible, setEnterpriseModalVisible] = useState(false);
    const [refundModalVisible, setRefundModalVisible] = useState(false);
    const [enterpriseForm] = Form.useForm();
    const [refundForm] = Form.useForm();
    const [chargebacks, setChargebacks] = useState([]);
    const [chargebacksPagination, setChargebacksPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [chargebacksLoading, setChargebacksLoading] = useState(false);
    const [chargebacksStatusFilter, setChargebacksStatusFilter] = useState(undefined);
    const [userChargebacks, setUserChargebacks] = useState([]);

    useEffect(() => {
        fetchSubscriptions(1);
        fetchPlans();
        fetchBillingMetrics();
        fetchCronHealth();
        fetchCronRuns(1);
        fetchChargebacks(1);
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await get_subscription_plansApi(true);
            if (response.data.status === "success") {
                setPlans(response.data.data?.plans || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchBillingMetrics = async () => {
        try {
            const response = await get_billing_metricsApi(30);
            if (response.data.status === "success") {
                setBillingMetrics(response.data.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCronHealth = async () => {
        try {
            const response = await get_billing_cron_healthApi();
            if (response.data.status === "success") {
                setCronHealth(response.data.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCronRuns = async (page = 1, status = cronRunsStatusFilter) => {
        try {
            setCronRunsLoading(true);
            const params = { page, page_size: cronRunsPagination.pageSize };
            if (status) params.status = status;
            const response = await list_billing_cron_runsApi(params);
            if (response.data.status === "success") {
                const data = response.data.data || {};
                setCronRuns(
                    (data.runs || []).map((r) => ({ ...r, key: r.id }))
                );
                setCronRunsPagination((prev) => ({
                    ...prev,
                    current: data.page || page,
                    total: data.total || 0,
                }));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCronRunsLoading(false);
        }
    };

    const fetchChargebacks = async (page = 1, status = chargebacksStatusFilter) => {
        try {
            setChargebacksLoading(true);
            const params = { page, page_size: chargebacksPagination.pageSize };
            if (status) params.status = status;
            const response = await list_chargebacksApi(params);
            if (response.data.status === "success") {
                const data = response.data.data || {};
                setChargebacks((data.items || []).map((c) => ({ ...c, key: c._id })));
                setChargebacksPagination((prev) => ({
                    ...prev,
                    current: data.page || page,
                    total: data.total || 0,
                }));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChargebacksLoading(false);
        }
    };

    const CHARGEBACK_STATUS_COLORS = {
        open: "red",
        reinstated: "green",
        confirmed_downgrade: "default",
    };

    const chargebackColumns = [
        {
            title: "Received",
            dataIndex: "received_at",
            key: "received_at",
            render: (v) => (v ? new Date(v).toLocaleString() : "—"),
        },
        {
            title: "User",
            dataIndex: "user_id",
            key: "user_id",
            render: (id) => (id ? id.slice(-8) : "—"),
        },
        {
            title: "Amount",
            key: "amount",
            render: (_, row) =>
                row.amount != null ? `${row.amount} ${row.currency || ""}` : "—",
        },
        {
            title: "Moyasar ID",
            dataIndex: "moyasar_payment_id",
            key: "moyasar_payment_id",
            render: (id) => id || "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={CHARGEBACK_STATUS_COLORS[status] || "default"}>{status}</Tag>
            ),
        },
        {
            title: "Resolved",
            dataIndex: "resolved_at",
            key: "resolved_at",
            render: (v) => (v ? new Date(v).toLocaleString() : "—"),
        },
    ];

    const CRON_STATUS_COLORS = {
        success: "green",
        partial: "orange",
        failed: "red",
        running: "blue",
    };

    const cronRunColumns = [
        {
            title: "Started",
            dataIndex: "started_at",
            key: "started_at",
            render: (v) => (v ? new Date(v).toLocaleString() : "—"),
        },
        {
            title: "Completed",
            dataIndex: "completed_at",
            key: "completed_at",
            render: (v) => (v ? new Date(v).toLocaleString() : "—"),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={CRON_STATUS_COLORS[status] || "default"}>{status}</Tag>
            ),
        },
        {
            title: "Counts",
            key: "counts",
            render: (_, record) => {
                const r = record.result || {};
                return [
                    `renewals: ${r.renewals ?? 0}`,
                    `dunning: ${r.dunning_actions ?? 0}`,
                    `downgrades: ${r.past_due_downgrades ?? 0}`,
                ].join(" · ");
            },
        },
        {
            title: "Failures",
            dataIndex: "failure_count",
            key: "failure_count",
            render: (count, record) =>
                count > 0 ? (
                    <Tag color="orange">{count}</Tag>
                ) : record.error ? (
                    <Tag color="red">error</Tag>
                ) : (
                    "0"
                ),
        },
        {
            title: "Error / failures",
            key: "error",
            ellipsis: true,
            render: (_, record) => {
                if (record.error) return record.error;
                const failures = record.result?.failures || [];
                if (!failures.length) return "—";
                return failures
                    .slice(0, 2)
                    .map((f) => `${f.job} (${f.user_id?.slice(-6)}): ${f.error}`)
                    .join("; ");
            },
        },
    ];

    const fetchSubscriptions = async (page = 1, filterOverrides = {}) => {
        try {
            setLoading(true);
            const activeFilters = { ...filters, ...filterOverrides };
            const params = {
                page,
                page_size: pagination.pageSize,
            };
            if (activeFilters.plan_id) params.plan_id = activeFilters.plan_id;
            if (activeFilters.status) params.status = activeFilters.status;
            if (activeFilters.expiring_in_days) {
                params.expiring_in_days = activeFilters.expiring_in_days;
            }

            const response = await list_user_subscriptionsApi(params);
            if (response.data.status === "success") {
                const data = response.data.data || {};
                setSubscriptions(
                    (data.subscriptions || []).map((s) => ({ ...s, key: s.id || s.user_id }))
                );
                setPagination((prev) => ({
                    ...prev,
                    current: data.page || page,
                    total: data.total || 0,
                }));
            } else {
                message.error(response.data.message || "Failed to load subscriptions");
            }
        } catch (error) {
            console.error(error);
            message.error("Error loading user subscriptions");
        } finally {
            setLoading(false);
        }
    };

    const openDetail = async (record) => {
        setSelectedUser(record);
        setDetailOpen(true);
        setDetailLoading(true);
        try {
            const [subRes, auditRes] = await Promise.all([
                get_user_subscriptionApi(record.user_id),
                list_billing_audit_logsApi({ user_id: record.user_id, limit: 20 }),
            ]);
            if (subRes.data.status === "success") {
                setDetailSubscription(subRes.data.data?.subscription || record);
                setUserChargebacks(subRes.data.data?.chargebacks || []);
            } else {
                setDetailSubscription(record);
                setUserChargebacks([]);
            }
            if (auditRes.data.status === "success") {
                setAuditLogs(auditRes.data.data?.logs || []);
            }
        } catch (error) {
            console.error(error);
            setDetailSubscription(record);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleAssign = async (values) => {
        try {
            setAssignLoading(true);
            const response = await assign_user_subscriptionApi(values);
            if (response.data.status === "success") {
                message.success("Plan assigned successfully");
                setAssignModalVisible(false);
                form.resetFields();
                fetchSubscriptions(pagination.current);
            } else {
                message.error(response.data.message || "Failed to assign plan");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to assign plan");
        } finally {
            setAssignLoading(false);
        }
    };

    const openCancelModal = (userId, immediate = false) => {
        setCancelTarget({ userId, immediate });
        cancelForm.resetFields();
        setCancelModalVisible(true);
    };

    const handleCancel = async (values) => {
        const { userId, immediate } = cancelTarget;
        if (!userId) return;
        try {
            setActionLoading(true);
            const response = await cancel_user_subscriptionApi({
                user_id: userId,
                immediate,
                reason: values.reason,
            });
            if (response.data.status === "success") {
                message.success(immediate ? "Subscription canceled immediately" : "Scheduled cancel at period end");
                setCancelModalVisible(false);
                cancelForm.resetFields();
                fetchSubscriptions(pagination.current);
                if (detailOpen && selectedUser?.user_id === userId) {
                    openDetail({ ...selectedUser, user_id: userId });
                }
            } else {
                message.error(response.data.message || "Failed to cancel");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Failed to cancel subscription");
        } finally {
            setActionLoading(false);
        }
    };

    const openQuotaModal = (userId, currentExtended = 0) => {
        setCancelTarget({ userId, immediate: false });
        quotaForm.setFieldsValue({ extended_quota_limit: currentExtended });
        setQuotaModalVisible(true);
    };

    const handleQuotaUpdate = async (values) => {
        const { userId } = cancelTarget;
        if (!userId) return;
        try {
            setActionLoading(true);
            const response = await update_user_subscription_quotaApi({
                user_id: userId,
                extended_quota_limit: values.extended_quota_limit,
            });
            if (response.data.status === "success") {
                message.success("Extended quota updated");
                setQuotaModalVisible(false);
                quotaForm.resetFields();
                fetchSubscriptions(pagination.current);
                if (detailOpen && selectedUser?.user_id === userId) {
                    openDetail({ ...selectedUser, user_id: userId });
                }
            } else {
                message.error(response.data.message || "Failed to update quota");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Failed to update extended quota");
        } finally {
            setActionLoading(false);
        }
    };

    const handleRenew = async (userId, chargePayment = true) => {
        try {
            setActionLoading(true);
            const response = await renew_user_subscriptionApi({
                user_id: userId,
                charge_payment: chargePayment,
            });
            if (response.data.status === "success") {
                message.success(
                    chargePayment ? "Subscription renewed and card charged" : "Subscription period extended"
                );
                fetchSubscriptions(pagination.current);
                if (detailOpen && selectedUser?.user_id === userId) {
                    openDetail({ ...selectedUser, user_id: userId });
                }
            } else {
                message.error(response.data.message || "Failed to renew");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Failed to renew subscription");
        } finally {
            setActionLoading(false);
        }
    };

    const handleRunBillingCron = async () => {
        try {
            setCronLoading(true);
            const response = await run_billing_cronApi();
            if (response.data.status === "success") {
                const r = response.data.data || {};
                message.success(
                    `Cron done: ${r.reminders_sent ?? 0} reminders, ${r.renewals ?? 0} renewals, ${r.past_due_retries ?? 0} retries, ${r.past_due_downgrades ?? 0} downgrades`
                );
                fetchSubscriptions(pagination.current);
                fetchCronHealth();
                fetchCronRuns(cronRunsPagination.current);
            } else {
                message.error(response.data.message || "Billing cron failed");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to run billing cron");
        } finally {
            setCronLoading(false);
        }
    };

    const handleEnterpriseCreate = async (values) => {
        try {
            setAssignLoading(true);
            const response = await create_enterprise_subscriptionApi(values);
            if (response.data.status === "success") {
                message.success("Enterprise subscription created (pending payment)");
                setEnterpriseModalVisible(false);
                enterpriseForm.resetFields();
                fetchSubscriptions(pagination.current);
            } else {
                message.error(response.data.message || "Failed to create enterprise subscription");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Failed to create enterprise subscription");
        } finally {
            setAssignLoading(false);
        }
    };

    const handleRefund = async (values) => {
        try {
            setActionLoading(true);
            const response = await refund_paymentApi(values);
            if (response.data.status === "success") {
                message.success("Payment refunded");
                setRefundModalVisible(false);
                refundForm.resetFields();
                if (detailOpen && selectedUser) {
                    openDetail(selectedUser);
                }
                fetchSubscriptions(pagination.current);
            } else {
                message.error(response.data.message || "Refund failed");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Refund failed");
        } finally {
            setActionLoading(false);
        }
    };

    const handleResolveDispute = async (action) => {
        if (!detailSubscription?.user_id) return;
        try {
            setActionLoading(true);
            const response = await resolve_billing_disputeApi({
                user_id: detailSubscription.user_id,
                action,
            });
            if (response.data.status === "success") {
                message.success(`Dispute: ${action}`);
                openDetail(selectedUser);
                fetchSubscriptions(pagination.current);
            } else {
                message.error(response.data.message || "Failed to resolve dispute");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Failed to resolve dispute");
        } finally {
            setActionLoading(false);
        }
    };

    const handleActivateEnterprise = async () => {
        if (!detailSubscription?.user_id) return;
        try {
            setActionLoading(true);
            const response = await activate_enterprise_subscriptionApi({
                user_id: detailSubscription.user_id,
            });
            if (response.data.status === "success") {
                message.success("Enterprise subscription activated");
                openDetail(selectedUser);
                fetchSubscriptions(pagination.current);
            } else {
                message.error(response.data.message || "Activation failed");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Activation failed");
        } finally {
            setActionLoading(false);
        }
    };

    const applyFilters = () => {
        fetchSubscriptions(1);
    };

    const columns = [
        { title: "User Email", dataIndex: "user_email", key: "user_email" },
        { title: "Plan", dataIndex: "plan_name", key: "plan_name" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status, record) => (
                <>
                    <Tag color={STATUS_COLORS[status] || "default"}>
                        {status === "disputed" ? "disputed" : status}
                    </Tag>
                    {record.cancel_at_period_end && <Tag color="orange">canceling</Tag>}
                    {record.billing_hold && <Tag color="red">chargeback hold</Tag>}
                </>
            ),
        },
        {
            title: "LLM Usage",
            key: "usage",
            render: (_, record) => {
                const u = record.usage || {};
                const effective = u.effective_limit ?? record.effective_quota_limit ?? u.limit ?? 0;
                return `${u.used ?? 0} / ${effective}`;
            },
        },
        {
            title: "Polars Usage",
            key: "polars_usage",
            render: (_, record) => {
                const u = record.usage || {};
                if (u.polars_unlimited) return `${u.polars_used ?? 0} / Unlimited`;
                const limit = u.polars_limit ?? "—";
                return `${u.polars_used ?? 0} / ${limit}`;
            },
        },
        {
            title: "Period End",
            dataIndex: "current_period_end",
            key: "current_period_end",
            render: (v) => (v ? new Date(v).toLocaleDateString() : "-"),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space wrap>
                    <Button size="small" onClick={() => openDetail(record)}>
                        Details
                    </Button>
                    <Button
                        size="small"
                        danger
                        loading={actionLoading}
                        onClick={() => openCancelModal(record.user_id, false)}
                    >
                        Cancel at end
                    </Button>
                    <Button
                        size="small"
                        danger
                        loading={actionLoading}
                        onClick={() => openCancelModal(record.user_id, true)}
                    >
                        Cancel now
                    </Button>
                    <Button
                        size="small"
                        type="primary"
                        loading={actionLoading}
                        onClick={() => handleRenew(record.user_id, true)}
                    >
                        Renew now
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <>
            {cronHealth && !cronHealth.healthy && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Billing cron unhealthy"
                    description={cronHealth.reason || "Check cron process and recent runs."}
                />
            )}
            {cronHealth?.healthy && (
                <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Billing cron healthy"
                    description={
                        cronHealth.cron?.last_run
                            ? `Last run: ${new Date(cronHealth.cron.last_run).toLocaleString()} UTC · Watchdog every ${cronHealth.cron?.watchdog_interval_hours ?? 4}h`
                            : "Scheduler running; awaiting first run."
                    }
                />
            )}
            {billingMetrics && (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="Active subs" value={billingMetrics.active_subscribers} /></Card>
                    </Col>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="MRR" value={billingMetrics.mrr} prefix="$" /></Card>
                    </Col>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="ARR" value={billingMetrics.arr} prefix="$" /></Card>
                    </Col>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="Churn rate %" value={billingMetrics.churn_rate_pct ?? "—"} suffix="%" /></Card>
                    </Col>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="Renewal success %" value={billingMetrics.renewal_success_rate_pct ?? "—"} suffix="%" /></Card>
                    </Col>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="Upgrade rate %" value={billingMetrics.upgrade_rate_pct ?? "—"} suffix="%" /></Card>
                    </Col>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="Downgrade rate %" value={billingMetrics.downgrade_rate_pct ?? "—"} suffix="%" /></Card>
                    </Col>
                    <Col xs={12} sm={8} md={3}>
                        <Card size="small"><Statistic title="Failed payment %" value={billingMetrics.failed_payment_rate_pct ?? "—"} suffix="%" /></Card>
                    </Col>
                </Row>
            )}
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col span={24}>
                    <Card
                        title="Billing cron execution history"
                        extra={
                            <Space>
                                <Select
                                    placeholder="Filter status"
                                    allowClear
                                    style={{ width: 140 }}
                                    value={cronRunsStatusFilter}
                                    onChange={(v) => {
                                        setCronRunsStatusFilter(v);
                                        fetchCronRuns(1, v);
                                    }}
                                >
                                    <Option value="success">Success</Option>
                                    <Option value="partial">Partial</Option>
                                    <Option value="failed">Failed</Option>
                                    <Option value="running">Running</Option>
                                </Select>
                                <Button onClick={() => { fetchCronHealth(); fetchCronRuns(cronRunsPagination.current); }}>
                                    Refresh
                                </Button>
                            </Space>
                        }
                    >
                        <Table
                            columns={cronRunColumns}
                            dataSource={cronRuns}
                            loading={cronRunsLoading}
                            size="small"
                            pagination={{
                                ...cronRunsPagination,
                                onChange: (page) => fetchCronRuns(page),
                            }}
                        />
                    </Card>
                </Col>
            </Row>
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col span={24}>
                    <Card
                        title="Chargeback history"
                        extra={
                            <Space>
                                <Select
                                    placeholder="Filter status"
                                    allowClear
                                    style={{ width: 160 }}
                                    value={chargebacksStatusFilter}
                                    onChange={(v) => {
                                        setChargebacksStatusFilter(v);
                                        fetchChargebacks(1, v);
                                    }}
                                >
                                    <Option value="open">Open</Option>
                                    <Option value="reinstated">Reinstated</Option>
                                    <Option value="confirmed_downgrade">Confirmed downgrade</Option>
                                </Select>
                                <Button onClick={() => fetchChargebacks(chargebacksPagination.current)}>
                                    Refresh
                                </Button>
                            </Space>
                        }
                    >
                        <Table
                            columns={chargebackColumns}
                            dataSource={chargebacks}
                            loading={chargebacksLoading}
                            size="small"
                            pagination={{
                                ...chargebacksPagination,
                                onChange: (page) => fetchChargebacks(page),
                            }}
                        />
                    </Card>
                </Col>
            </Row>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card
                        title="User Subscriptions"
                        extra={
                            <Space>
                                <Button loading={cronLoading} onClick={handleRunBillingCron}>
                                    Run billing cron
                                </Button>
                                <Button onClick={() => setEnterpriseModalVisible(true)}>
                                    Enterprise (invoice)
                                </Button>
                                <Button onClick={() => setRefundModalVisible(true)}>
                                    Refund payment
                                </Button>
                                <Button type="primary" onClick={() => setAssignModalVisible(true)}>
                                    Assign Plan
                                </Button>
                            </Space>
                        }
                    >
                        <Space wrap style={{ marginBottom: 16 }}>
                            <Select
                                placeholder="Filter by plan"
                                allowClear
                                style={{ width: 180 }}
                                value={filters.plan_id}
                                onChange={(v) => setFilters((f) => ({ ...f, plan_id: v }))}
                            >
                                {plans.map((p) => (
                                    <Option key={p._id} value={p._id}>
                                        {p.name}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                placeholder="Status"
                                allowClear
                                style={{ width: 140 }}
                                value={filters.status}
                                onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                            >
                                <Option value="active">Active</Option>
                                <Option value="past_due">Past due</Option>
                                <Option value="pending_payment">Pending payment</Option>
                                <Option value="canceled">Canceled</Option>
                            </Select>
                            <Select
                                placeholder="Expiring soon"
                                allowClear
                                style={{ width: 160 }}
                                value={filters.expiring_in_days}
                                onChange={(v) => setFilters((f) => ({ ...f, expiring_in_days: v }))}
                            >
                                <Option value={7}>Within 7 days</Option>
                                <Option value={30}>Within 30 days</Option>
                            </Select>
                            <Button type="primary" onClick={applyFilters}>
                                Apply
                            </Button>
                        </Space>

                        <Table
                            columns={columns}
                            dataSource={subscriptions}
                            loading={loading}
                            pagination={{
                                ...pagination,
                                onChange: (page) => fetchSubscriptions(page),
                            }}
                        />
                    </Card>
                </Col>
            </Row>

            <Drawer
                title={selectedUser ? `Subscription — ${selectedUser.user_email}` : "Subscription"}
                width={640}
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
            >
                {detailLoading ? (
                    <Spin />
                ) : detailSubscription ? (
                    <>
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Subscription ID">
                                {detailSubscription.id || "—"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Plan">{detailSubscription.plan_name}</Descriptions.Item>
                            <Descriptions.Item label="Status">{detailSubscription.status}</Descriptions.Item>
                            <Descriptions.Item label="LLM usage (current period)">
                                {detailSubscription.usage?.used ?? 0} /{" "}
                                {detailSubscription.usage?.effective_limit ??
                                    detailSubscription.effective_quota_limit ??
                                    detailSubscription.usage?.limit ??
                                    0}
                                {detailSubscription.usage?.period_key
                                    ? ` · ${detailSubscription.usage.period_key}`
                                    : ""}
                            </Descriptions.Item>
                            {detailSubscription.extended_quota_limit > 0 && (
                                <Descriptions.Item label="Extended LLM quota">
                                    +{detailSubscription.extended_quota_limit}
                                </Descriptions.Item>
                            )}
                            {(detailSubscription.extended_polars_quota_limit ?? 0) > 0 && (
                                <Descriptions.Item label="Extended Polars quota">
                                    +{detailSubscription.extended_polars_quota_limit}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Polars summary usage">
                                {detailSubscription.usage?.polars_unlimited
                                    ? `${detailSubscription.usage?.polars_used ?? 0} / Unlimited`
                                    : `${detailSubscription.usage?.polars_used ?? 0} / ${detailSubscription.usage?.polars_limit ?? "—"}`}
                            </Descriptions.Item>
                            {detailSubscription.quota_bonus_current_cycle > 0 && (
                                <Descriptions.Item label="Quota bonus (this cycle)">
                                    +{detailSubscription.quota_bonus_current_cycle}
                                </Descriptions.Item>
                            )}
                            {detailSubscription.scheduled_plan_id && (
                                <Descriptions.Item label="Scheduled downgrade">
                                    {detailSubscription.scheduled_plan_name || detailSubscription.scheduled_plan_id}
                                    {detailSubscription.scheduled_plan_change_at
                                        ? ` on ${new Date(detailSubscription.scheduled_plan_change_at).toLocaleString()}`
                                        : ""}
                                </Descriptions.Item>
                            )}
                            {detailSubscription.dunning_reminder_day > 0 && (
                                <Descriptions.Item label="Dunning step">
                                    Day {detailSubscription.dunning_reminder_day}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Period start">
                                {detailSubscription.current_period_start
                                    ? new Date(detailSubscription.current_period_start).toLocaleString()
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Period end">
                                {detailSubscription.current_period_end
                                    ? new Date(detailSubscription.current_period_end).toLocaleString()
                                    : "-"}
                            </Descriptions.Item>
                            {detailSubscription.billing_hold && (
                                <Descriptions.Item label="Billing hold">
                                    {detailSubscription.billing_hold_reason || "dispute"}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Payment method">
                                {detailSubscription.has_payment_method ? "Card on file" : "None"}
                            </Descriptions.Item>
                            {detailSubscription.past_due_at && (
                                <Descriptions.Item label="Past due since">
                                    {new Date(detailSubscription.past_due_at).toLocaleString()}
                                </Descriptions.Item>
                            )}
                            {detailSubscription.past_due_grace_ends_at && (
                                <Descriptions.Item label="Grace ends">
                                    {new Date(detailSubscription.past_due_grace_ends_at).toLocaleString()}
                                </Descriptions.Item>
                            )}
                            {detailSubscription.last_payment_id && (
                                <Descriptions.Item label="Last payment ID">
                                    {detailSubscription.last_payment_id}
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        {(detailSubscription.usage_history || []).length > 0 && (
                            <>
                                <h4 style={{ marginTop: 20, marginBottom: 8 }}>Usage history</h4>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={{ pageSize: 8 }}
                                    dataSource={detailSubscription.usage_history}
                                    columns={[
                                        {
                                            title: "Period",
                                            dataIndex: "period_key",
                                            key: "period_key",
                                        },
                                        {
                                            title: "Plan",
                                            dataIndex: "plan_name",
                                            key: "plan_name",
                                            render: (name) => name || "—",
                                        },
                                        {
                                            title: "LLM",
                                            key: "llm",
                                            render: (_, row) => row.request_count ?? 0,
                                        },
                                        {
                                            title: "Polars",
                                            key: "polars",
                                            render: (_, row) => row.polars_request_count ?? 0,
                                        },
                                        {
                                            title: "Subscription",
                                            dataIndex: "subscription_id",
                                            key: "subscription_id",
                                            render: (id) =>
                                                id && id === detailSubscription.id ? (
                                                    <Tag color="blue">current</Tag>
                                                ) : (
                                                    (id || "—").slice(-8)
                                                ),
                                        },
                                        {
                                            title: "Last activity",
                                            dataIndex: "updated_at",
                                            key: "updated_at",
                                            render: (v) =>
                                                v ? new Date(v).toLocaleString() : "—",
                                        },
                                    ]}
                                />
                            </>
                        )}

                        <Space style={{ marginTop: 16 }} wrap>
                            <Button
                                loading={actionLoading}
                                onClick={() =>
                                    openQuotaModal(
                                        detailSubscription.user_id,
                                        detailSubscription.extended_quota_limit ?? 0
                                    )
                                }
                            >
                                Set extended LLM quota
                            </Button>
                            <Button
                                danger
                                loading={actionLoading}
                                onClick={() => openCancelModal(detailSubscription.user_id, false)}
                            >
                                Cancel at period end
                            </Button>
                            <Button
                                danger
                                loading={actionLoading}
                                onClick={() => openCancelModal(detailSubscription.user_id, true)}
                            >
                                Cancel immediately
                            </Button>
                            <Button
                                type="primary"
                                loading={actionLoading}
                                onClick={() => handleRenew(detailSubscription.user_id, true)}
                            >
                                Manual renew (charge card)
                            </Button>
                            <Button
                                loading={actionLoading}
                                onClick={() => handleRenew(detailSubscription.user_id, false)}
                            >
                                Extend period (no charge)
                            </Button>
                            {detailSubscription.status === "pending_payment" && (
                                <Button
                                    type="primary"
                                    loading={actionLoading}
                                    onClick={handleActivateEnterprise}
                                >
                                    Mark enterprise paid
                                </Button>
                            )}
                            {detailSubscription.billing_hold && (
                                <>
                                    <Button
                                        loading={actionLoading}
                                        onClick={() => handleResolveDispute("reinstate")}
                                    >
                                        Reinstate after dispute
                                    </Button>
                                    <Button
                                        danger
                                        loading={actionLoading}
                                        onClick={() => handleResolveDispute("confirm_downgrade")}
                                    >
                                        Confirm downgrade
                                    </Button>
                                </>
                            )}
                        </Space>

                        {(userChargebacks || []).length > 0 && (
                            <>
                                <h4 style={{ marginTop: 24 }}>Chargeback history</h4>
                                <Table
                                    size="small"
                                    pagination={false}
                                    dataSource={userChargebacks.map((c) => ({ ...c, key: c._id }))}
                                    columns={chargebackColumns}
                                />
                            </>
                        )}

                        {auditLogs.length > 0 && (
                            <>
                                <h4 style={{ marginTop: 24 }}>Audit log</h4>
                                <Table
                                    size="small"
                                    pagination={false}
                                    dataSource={auditLogs.map((l) => ({ ...l, key: l._id }))}
                                    columns={[
                                        { title: "Action", dataIndex: "action", key: "action" },
                                        {
                                            title: "Admin",
                                            dataIndex: "admin_email",
                                            key: "admin_email",
                                        },
                                        {
                                            title: "Reason",
                                            key: "reason",
                                            render: (_, record) => record.details?.reason || "-",
                                        },
                                        {
                                            title: "When",
                                            dataIndex: "created_at",
                                            key: "created_at",
                                            render: (v) => (v ? new Date(v).toLocaleString() : "-"),
                                        },
                                    ]}
                                />
                            </>
                        )}
                    </>
                ) : null}
            </Drawer>

            <Modal
                title="Assign Subscription Plan"
                open={assignModalVisible}
                onCancel={() => setAssignModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={assignLoading}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleAssign}>
                    <Form.Item
                        name="user_id"
                        label="User ID"
                        rules={[{ required: true, message: "User ID is required" }]}
                    >
                        <Input placeholder="MongoDB user _id" />
                    </Form.Item>
                    <Form.Item
                        name="plan_id"
                        label="Plan"
                        rules={[{ required: true, message: "Select a plan" }]}
                    >
                        <Select placeholder="Select plan">
                            {plans.map((p) => (
                                <Option key={p._id} value={p._id}>
                                    {p.name} ({p.general_quota_limit} req/mo)
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={cancelTarget.immediate ? "Cancel subscription immediately" : "Cancel at period end"}
                open={cancelModalVisible}
                onCancel={() => setCancelModalVisible(false)}
                onOk={() => cancelForm.submit()}
                confirmLoading={actionLoading}
                destroyOnClose
            >
                <Form form={cancelForm} layout="vertical" onFinish={handleCancel}>
                    <Form.Item
                        name="reason"
                        label="Cancellation reason"
                        rules={[
                            { required: true, message: "Reason is required" },
                            { min: 3, message: "Reason must be at least 3 characters" },
                        ]}
                    >
                        <Input.TextArea rows={4} placeholder="Why is this subscription being canceled?" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Extended LLM quota"
                open={quotaModalVisible}
                onCancel={() => setQuotaModalVisible(false)}
                onOk={() => quotaForm.submit()}
                confirmLoading={actionLoading}
                destroyOnClose
            >
                <Form form={quotaForm} layout="vertical" onFinish={handleQuotaUpdate}>
                    <Form.Item
                        name="extended_quota_limit"
                        label="Extra LLM requests per billing period"
                        extra="Added on top of the plan limit and any upgrade carryover. Polars summaries are unlimited on paid plans."
                        rules={[{ required: true, message: "Extended quota is required" }]}
                    >
                        <InputNumber min={0} max={100000} style={{ width: "100%" }} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Enterprise subscription (invoice before pay)"
                open={enterpriseModalVisible}
                onCancel={() => setEnterpriseModalVisible(false)}
                onOk={() => enterpriseForm.submit()}
                confirmLoading={assignLoading}
                destroyOnClose
            >
                <Form form={enterpriseForm} layout="vertical" onFinish={handleEnterpriseCreate}>
                    <Form.Item name="user_id" label="User ID" rules={[{ required: true }]}>
                        <Input placeholder="MongoDB user _id" />
                    </Form.Item>
                    <Form.Item name="plan_id" label="Plan" rules={[{ required: true }]}>
                        <Select placeholder="Select plan">
                            {plans.filter((p) => Number(p.amount) > 0).map((p) => (
                                <Option key={p._id} value={p._id}>{p.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="po_number" label="PO number">
                        <Input />
                    </Form.Item>
                    <Form.Item name="billing_contact_email" label="Billing contact email">
                        <Input type="email" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Refund payment"
                open={refundModalVisible}
                onCancel={() => setRefundModalVisible(false)}
                onOk={() => refundForm.submit()}
                confirmLoading={actionLoading}
                destroyOnClose
            >
                <Form form={refundForm} layout="vertical" onFinish={handleRefund}>
                    <Form.Item name="payment_id" label="Payment ID" rules={[{ required: true }]}>
                        <Input placeholder="billing_payments _id" />
                    </Form.Item>
                    <Form.Item
                        name="reason"
                        label="Reason"
                        rules={[{ required: true, min: 3, message: "Reason required (min 3 chars)" }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default UserSubscriptionsManagement;
